import { Link } from "react-router-dom";
import { X, ShoppingCart, Plus, Minus, Trash2 } from "lucide-react";
import { useCart, eur } from "@/context/CartContext";
import { mediaUrl } from "@/lib/api";

export default function CartDrawer() {
    const { items, open, setOpen, setQty, remove, subtotalCents, vatCents, totalCents, vatPercent } = useCart();
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[60]" data-testid="cart-drawer">
            <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
            <aside className="absolute right-0 top-0 h-screen w-full max-w-md bg-white shadow-2xl flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
                    <h2 className="font-heading text-lg font-extrabold text-primary inline-flex items-center gap-2">
                        <ShoppingCart className="h-5 w-5" /> Carrinho
                    </h2>
                    <button onClick={() => setOpen(false)} data-testid="cart-close" className="h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-muted text-primary">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
                    {items.length === 0 ? (
                        <p className="text-center text-muted-foreground py-16">O seu carrinho está vazio.</p>
                    ) : (
                        items.map((it) => (
                            <div key={it.line_key} className="flex gap-3 items-start" data-testid={`cart-item-${it.slug || it.product_id || it.bundle_id}`}>
                                <div className="h-16 w-16 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                                    {it.image_url ? <img src={mediaUrl(it.image_url)} alt={it.name} className="h-full w-full object-cover" /> : null}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-primary truncate">
                                        {it.bundle_id ? <span className="mr-1 rounded-full bg-secondary/15 px-1.5 py-0.5 text-[9px] font-bold text-primary/70 uppercase tracking-wider">Pack</span> : null}
                                        {it.name}
                                    </div>
                                    {it.options_summary ? (
                                        <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{it.options_summary}</div>
                                    ) : null}
                                    <div className="text-sm text-muted-foreground">
                                        {eur(it.price_cents)}
                                        <span className="ml-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">s/IVA</span>
                                    </div>
                                    <div className="mt-2 flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setQty(it.line_key, it.quantity - 1)}
                                            data-testid={`cart-qty-minus-${it.slug || it.product_id || it.bundle_id}`}
                                            aria-label="Diminuir quantidade"
                                            className="h-7 w-7 inline-flex items-center justify-center rounded border border-border hover:bg-muted"
                                        >
                                            <Minus className="h-3.5 w-3.5" />
                                        </button>
                                        <span className="w-8 text-center text-sm font-bold">{it.quantity}</span>
                                        <button
                                            type="button"
                                            onClick={() => setQty(it.line_key, it.quantity + 1)}
                                            data-testid={`cart-qty-plus-${it.slug || it.product_id || it.bundle_id}`}
                                            aria-label="Aumentar quantidade"
                                            className="h-7 w-7 inline-flex items-center justify-center rounded border border-border hover:bg-muted"
                                        >
                                            <Plus className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => remove(it.line_key)}
                                            data-testid={`cart-remove-${it.slug || it.product_id || it.bundle_id}`}
                                            aria-label="Remover item"
                                            className="ml-auto h-7 w-7 inline-flex items-center justify-center rounded text-red-500 hover:bg-red-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="border-t border-border px-5 py-4 space-y-2 shrink-0 bg-white">
                    {items.length > 0 && (
                        <>
                            <div className="flex items-center justify-between text-sm text-muted-foreground">
                                <span>Subtotal</span>
                                <span data-testid="cart-subtotal">{eur(subtotalCents)}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm text-muted-foreground">
                                <span>IVA ({vatPercent}%)</span>
                                <span data-testid="cart-vat">{eur(vatCents)}</span>
                            </div>
                            <div className="border-t border-border pt-2 flex items-center justify-between text-primary">
                                <span className="font-semibold">Total</span>
                                <span data-testid="cart-total" className="font-heading text-xl font-extrabold">{eur(totalCents)}</span>
                            </div>
                        </>
                    )}
                    {items.length === 0 && (
                        <div className="flex items-center justify-between text-primary">
                            <span className="font-semibold">Total</span>
                            <span data-testid="cart-total" className="font-heading text-xl font-extrabold">{eur(0)}</span>
                        </div>
                    )}
                    <Link
                        to="/checkout"
                        onClick={() => setOpen(false)}
                        data-testid="cart-checkout-btn"
                        className={`block w-full text-center rounded-md bg-secondary px-5 py-3 text-sm font-bold text-secondary-foreground transition hover:-translate-y-0.5 ${items.length === 0 ? "pointer-events-none opacity-50" : ""}`}
                    >
                        Finalizar compra
                    </Link>
                </div>
            </aside>
        </div>
    );
}
